import { useCallback, useEffect, useState } from 'react';

import { getHospitalDateFormatByHspId } from '../config';
import {
  bindAdditionalItemsData,
  bindMedRsvData,
  bindPlaceToGoData,
  bindReceiptItemsData,
  medicalReceiptFormatDateByDash,
  medicalReceiptFormatDateTimeByDash,
  medicalReceiptFormatNumber,
  medicalReceiptYearMonthDaySelector,
} from '../lib';
import { medicalReceiptFormatDateTimeByDash2 } from '../lib/format';

export const useReceipt = (receiptData, hspId, ref) => {
  const [scale, setScale] = useState(0.8);
  const [isZoomed, setIsZoomed] = useState(false);
  const [showZoomIcon, setShowZoomIcon] = useState(true);

  // 줌 관련 핸들러
  const zoomHandlers = useCallback(
    () => ({
      onZoom: ({ state }) => {
        setScale(state.scale);
        setIsZoomed(state.scale <= 1);
      },
      onTransformed: ({ state }) => {
        setScale(state.scale);
        setIsZoomed(state.scale <= 0.8);
      },
    }),
    [],
  );

  // 데이터 바인딩 로직
  const bindDataToElements = useCallback(() => {
    const document = ref.current.ownerDocument;

    const hospitalDateFormat = getHospitalDateFormatByHspId(hspId);

    const dataMapping = {
      // 영수증 타이틀
      receipt_nm: receiptData.receipt_nm, // 영수증 명
      receipt_type: receiptData.receipt_type, // 영수증 타입

      // 테이블 헤더
      receive_payment_id: receiptData.receive_payment_id,
      ctr_nm: receiptData.ctr_nm,

      // Patient information
      pt_no: receiptData.pt_no, // 환자번호
      pt_nm: receiptData.pt_nm, // 환자이름
      med_str_dt: hospitalDateFormat(receiptData.med_str_dt), // 진료시작일
      med_end_dt: hospitalDateFormat(receiptData.med_end_dt), // 진료종료일
      night_yn: receiptData.night_yn, // 야간진료 여부
      holiday_yn: receiptData.holiday_yn, // 공휴일 진료 여부
      sub_pt_gb: receiptData.sub_pt_gb, // 환자구분

      // Department and receipt information
      dept_nm: receiptData.dept_nm, // 진료과목
      medr_nm: receiptData.medr_nm, // 진료의명
      disease_cd: receiptData.disease_cd, // 진병군(DRG) 번호
      ward_room: receiptData.ward_room, // 병동/실
      pt_gb: receiptData.pt_gb, // 환자구분
      receipt_id: receiptData.receipt_id, // 영수증 번호

      // 금액 산정 내용
      over_limit_price: medicalReceiptFormatNumber(receiptData.over_limit_price), // 초과금액
      total_price: medicalReceiptFormatNumber(receiptData.total_price),
      total_insurance_price: medicalReceiptFormatNumber(receiptData.total_insurance_price),
      total_partial_patient_responsibility_price: medicalReceiptFormatNumber(
        receiptData.total_partial_patient_responsibility_price,
      ),
      already_paid_price: medicalReceiptFormatNumber(receiptData.already_paid_price),
      price_to_be_paid: medicalReceiptFormatNumber(receiptData.price_to_be_paid),
      price_paid_card: medicalReceiptFormatNumber(receiptData.price_paid_card),
      price_paid_cash: medicalReceiptFormatNumber(receiptData.price_paid_cash),
      price_paid_cash_receipt: medicalReceiptFormatNumber(receiptData.price_paid_cash_receipt),
      price_paid_total: medicalReceiptFormatNumber(receiptData.price_paid_total),
      price_not_paid_discounted: medicalReceiptFormatNumber(receiptData.price_not_paid_discounted),
      price_not_paid_postpaid: medicalReceiptFormatNumber(receiptData.price_not_paid_postpaid),

      // 신용 카드 내역
      business_registration_number: receiptData.business_registration_number,
      card_number: receiptData.card_number,
      card_company: receiptData.card_company,
      installment_month: receiptData.installment_month === '0' ? '일시불' : receiptData.installment_month,
      approval_number: receiptData.approval_number,

      // 현금 영수증
      cash_receipt_id_number: receiptData.cash_receipt_id_number,
      cash_receipt_approval_number: receiptData.cash_receipt_approval_number,

      // 영수증 발행일시
      issue_datetime: receiptData.issue_datetime, // 발행 일시
      payee_name: receiptData.payee_name, // 수납자 이름

      // 부가 항목
      additional_content_title: receiptData.additional_content_title, // 부가 항목 제목
      additional_content_value: receiptData.additional_content_value, // 부가 항목 값
      campaign: receiptData.campaign, // 캠페인

      // 주차바코드 - 바인딩에서 제외 (JsBarcode로 직접 생성)
      // prk_brcd_data: receiptData.prk_brcd_data, // 주차바코드

      // 투약 정보
      phr_info: receiptData.phr_info, // 투약 정보

      payment_dtm: medicalReceiptFormatDateTimeByDash(receiptData.payment_dtm), // 결제일시
      issue_datetime_year: medicalReceiptYearMonthDaySelector(receiptData.issue_datetime).year, // 발행일시
      issue_datetime_month: medicalReceiptYearMonthDaySelector(receiptData.issue_datetime).month, // 발행일시
      issue_datetime_date: medicalReceiptYearMonthDaySelector(receiptData.issue_datetime).day, // 발행일시
      rpst_nm: receiptData.rpst_nm, //대표자명
      cutoff_price: receiptData.cutoff_price, // 끝수처리조정금

      // 진료예약증
      med_rsv: receiptData.med_rsv,
      med_rsv_dt_year: medicalReceiptYearMonthDaySelector(receiptData.med_rsv_dt).year,
      med_rsv_dt_month: medicalReceiptYearMonthDaySelector(receiptData.med_rsv_dt).month2,
      med_rsv_dt_date: medicalReceiptYearMonthDaySelector(receiptData.med_rsv_dt).day2,

      // 가야할 장소
      place_to_go: receiptData.place_to_go,
      place_to_go_dt: medicalReceiptFormatDateByDash(receiptData.med_str_dt),

      // 투약 정보
      drug_voucher_drug_cd: receiptData.drug_voucher.drug_cd,
      drug_voucher_pt_info: receiptData.drug_voucher.pt_info,
      drug_voucher_dept_info: receiptData.drug_voucher.dept_info,
      drug_voucher_op_dtm: medicalReceiptFormatDateTimeByDash2(receiptData.drug_voucher.op_dtm),

      // 주차 가이드
      prk_guide: receiptData.prk_guide,
    };

    // Iterate through the mapping and update elements
    Object.entries(dataMapping).forEach(([key, value]) => {
      // Try to find elements by name
      const elementsByName = document.getElementsByName(key);

      if (elementsByName.length > 0) {
        elementsByName.forEach((element) => {
          //야간 공휴일 체크박스
          if (key === 'night_yn' || key === 'holiday_yn') {
            element.classList.toggle('check', value === 'Y');
          } else if (key === 'receipt_type') {
            // 영수증 타입 체크박스
            document.querySelector('li:nth-child(2) .squre-box').textContent = '';
            document.querySelector('li:nth-child(3) .squre-box').textContent = '';
            if (receiptData.receipt_type === '계산서') {
              document.querySelector('li:nth-child(2) .squre-box').classList.toggle('check', true);
              document.querySelector('li:nth-child(3) .squre-box').classList.toggle('check', false);
            } else if (receiptData.receipt_type === '영수증') {
              document.querySelector('li:nth-child(2) .squre-box').classList.toggle('check', false);
              document.querySelector('li:nth-child(3) .squre-box').classList.toggle('check', true);
            }
          } else if (key === 'phr_info' || key === 'additional_content_value' || key === 'prk_guide') {
            // \n을 <br>로 변환 필요한 데이터
            element.innerHTML = value ? value.replace(/\n/g, '<br>') : '';
          } else {
            element.textContent = value || '';
          }
        });
      }
    });

    // receipt_items가 있을 때만 bindReceiptItemsData 호출
    if (receiptData.receipt_items && receiptData.receipt_items.length > 0) {
      try {
        bindReceiptItemsData(receiptData, ref.current, hspId);
      } catch (error) {
        console.warn('Failed to bind receipt items data:', error);
      }
    }

    // additional_items가 있을 때만 bindAdditionalItemsData 호출
    if (receiptData.additional_items && receiptData.additional_items.length > 0) {
      try {
        bindAdditionalItemsData(receiptData, ref.current);
      } catch (error) {
        console.warn('Failed to bind additional items data:', error);
      }
    }

    //med_rsv가 있을 때만 bindMedRsvData 호출
    if (receiptData.med_rsv && receiptData.med_rsv.length > 0) {
      try {
        bindMedRsvData(receiptData, ref.current);
      } catch (error) {
        console.warn('Failed to bind med rsv data:', error);
      }
    }

    //place_to_go가 있을 때만 bindPlaceToGoData 호출
    if (receiptData.place_to_go && receiptData.place_to_go.length > 0) {
      try {
        bindPlaceToGoData(receiptData, ref.current);
      } catch (error) {
        console.warn('Failed to bind place to go data:', error);
      }
    }
  }, [receiptData, hspId, ref]);

  // 줌 아이콘 타이머
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowZoomIcon(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  return {
    // 상태
    scale,
    isZoomed,
    setIsZoomed,
    showZoomIcon,
    setShowZoomIcon,

    // 핸들러
    zoomHandlers: zoomHandlers(),

    // 유틸리티
    bindDataToElements,
  };
};
